/**
 * Sistema di paginazione per il FilterHandler
 *
 * @description
 * La classe Pagination gestisce tutti gli aspetti della paginazione per le query:
 * - Calcolo automatico di skip e top per OData
 * - Serializzazione/deserializzazione da/verso query string
 * - Integrazione con il contesto delle query
 */

import type { QueryParams, ODataContext } from '../types';

/**
 * Interfaccia per oggetti di paginazione
 *
 * @description
 * Definisce la struttura base per oggetti di paginazione,
 * utilizzabile sia con la classe Pagination che con oggetti plain
 *
 * @example
 * // Con classe
 * const pagination: IPaginationData = new Pagination(10, 2);
 *
 * @example
 * // Con oggetto plain (per Vue 3 Composition API)
 * const pagination: IPaginationData = { perPage: 10, page: 2 };
 */
export interface IPaginationData {
	/** Numero di elementi per pagina */
	perPage: number;
	/** Numero della pagina corrente (1-based) */
	page: number;
}

/**
 * Classe per la gestione della paginazione
 *
 * @description
 * Pagination fornisce un'API semplice per gestire la paginazione in query OData.
 * Calcola automaticamente i parametri $skip e $top e si integra seamlessly
 * con il sistema di filtri.
 *
 * @example
 * // Paginazione base (25 elementi per pagina, pagina 1)
 * const pagination = new Pagination();
 * console.log(pagination.perPage); // 25
 * console.log(pagination.page);    // 1
 *
 * @example
 * // Paginazione personalizzata
 * const pagination = new Pagination(50, 3);
 * console.log(pagination.perPage); // 50
 * console.log(pagination.page);    // 3
 * // Mostra elementi da 101 a 150 (skip: 100, top: 50)
 *
 * @example
 * // Uso con filterHandler
 * const dataPagination = new Pagination(10, 2);
 * // Nel buildOdataQuery(), questo genererà: $skip=10&$top=10
 *
 * @example
 * // Integrazione con componenti UI
 * <Pagination
 *   :total="searchResultTotal"
 *   :page-size="dataPagination.perPage"
 *   :current-page="dataPagination.page"
 *   @size-change="onPaginationPerPageChange"
 *   @current-change="onPaginationPageChange"
 * />
 *
 * @category Core
 * @since 1.0.0
 */
export class Pagination {
	/**
	 * Numero di elementi per pagina
	 * @public
	 */
	public perPage: number = 25;

	/**
	 * Numero della pagina corrente (1-based)
	 * @public
	 */
	public page: number = 1;

	/**
	 * Crea una nuova istanza di Pagination
	 *
	 * @param perPage - Numero di elementi per pagina (default: 25)
	 * @param page - Numero della pagina corrente (default: 1)
	 *
	 * @example
	 * // Paginazione con default (25 per pagina, pagina 1)
	 * const pagination = new Pagination();
	 *
	 * @example
	 * // Paginazione personalizzata
	 * const pagination = new Pagination(50, 2);
	 *
	 * @public
	 */
	constructor(perPage: number = 25, page: number = 1) {
		this.perPage = perPage;
		this.page = page;
	}

	/**
	 * Calcola il parametro $top per OData
	 * Corrisponde al numero di elementi da restituire
	 *
	 * @returns Numero di elementi da restituire
	 *
	 * @example
	 * const pagination = new Pagination(25, 1);
	 * pagination._top; // 25
	 *
	 * @private
	 * @readonly
	 */
	private get _top(): number {
		return this.perPage;
	}

	/**
	 * Calcola il parametro $skip per OData
	 * Corrisponde al numero di elementi da saltare
	 *
	 * @returns Numero di elementi da saltare
	 *
	 * @example
	 * const pagination = new Pagination(25, 1);
	 * pagination._skip; // 0 (prima pagina)
	 *
	 * const pagination2 = new Pagination(25, 3);
	 * pagination2._skip; // 50 (salta le prime 2 pagine = 50 elementi)
	 *
	 * @private
	 * @readonly
	 */
	private get _skip(): number {
		return this.perPage * (this.page - 1);
	}

	/**
	 * Aggiunge i parametri di paginazione al contesto OData
	 * Imposta $top e $skip nel contesto della query
	 *
	 * @param context - Contesto OData da modificare
	 *
	 * @example
	 * const context = {};
	 * const pagination = new Pagination(20, 3);
	 * pagination._setContext(context);
	 * // context ora contiene: { top: 20, skip: 40 }
	 *
	 * @protected
	 */
	public _setContext(context: ODataContext): void {
		context.top = this._top;
		context.skip = this._skip;
	}

	/**
	 * Legge i parametri di paginazione dalla query string
	 * Aggiorna page e perPage dai parametri URL
	 *
	 * @param query - Parametri della query string
	 *
	 * @example
	 * // URL: ?page=3&perPage=50
	 * const pagination = new Pagination();
	 * pagination._fromQuery({ page: '3', perPage: '50' });
	 * // pagination.page = 3, pagination.perPage = 50
	 *
	 * @example
	 * // Gestione valori invalidi
	 * pagination._fromQuery({ page: 'abc', perPage: '-5' });
	 * // Mantiene i valori precedenti se la conversione fallisce
	 *
	 * @protected
	 */
	public _fromQuery(query: QueryParams): void {
		// Aggiorna il numero di pagina se presente e valido
		if (query.page) {
			const pageNum = Number.parseInt(String(query.page), 10);
			if (!isNaN(pageNum) && pageNum >= 1) {
				this.page = pageNum;
			}
		}

		// Aggiorna il numero di elementi per pagina se presente e valido
		if (query.perPage) {
			const perPageNum = Number.parseInt(String(query.perPage), 10);
			if (!isNaN(perPageNum) && perPageNum >= 1 && perPageNum <= 1000) {
				this.perPage = perPageNum;
			}
		}
	}

	/**
	 * Scrive i parametri di paginazione nella query string
	 * Aggiunge page e perPage ai parametri URL
	 *
	 * @param query - Oggetto query string da modificare
	 *
	 * @example
	 * const query = {};
	 * const pagination = new Pagination(30, 2);
	 * pagination._toQuery(query);
	 * // query ora contiene: { page: 2, perPage: 30 }
	 *
	 * @protected
	 */
	public _toQuery(query: QueryParams): void {
		query.page = this.page;
		query.perPage = this.perPage;
	}

	/**
	 * Restituisce una descrizione testuale della paginazione corrente
	 * Utile per il debug e per mostrare informazioni all'utente
	 *
	 * @returns Stringa descrittiva della paginazione
	 *
	 * @example
	 * const pagination = new Pagination(25, 3);
	 * pagination.formatAsText(); // "Pagina 3, 25 per pagina (elementi 51-75)"
	 *
	 * @example
	 * const pagination = new Pagination(10, 1);
	 * pagination.formatAsText(); // "Pagina 1, 10 per pagina (elementi 1-10)"
	 *
	 * @public
	 */
	public formatAsText(): string {
		const startElement = this._skip + 1;
		const endElement = this._skip + this.perPage;

		return `Pagina ${this.page}, ${this.perPage} per pagina (elementi ${startElement}-${endElement})`;
	}

	/**
	 * Reimposta la paginazione alla prima pagina
	 * Mantiene il perPage corrente
	 *
	 * @example
	 * const pagination = new Pagination(25, 5);
	 * pagination.reset();
	 * // pagination.page = 1, pagination.perPage = 25
	 *
	 * @public
	 */
	public reset(): void {
		this.page = 1;
	}

	/**
	 * Imposta una nuova dimensione per pagina e reimposta alla prima pagina
	 *
	 * @param newPerPage - Nuovo numero di elementi per pagina
	 *
	 * @example
	 * const pagination = new Pagination(25, 3);
	 * pagination.setPerPage(50);
	 * // pagination.perPage = 50, pagination.page = 1
	 *
	 * @public
	 */
	public setPerPage(newPerPage: number): void {
		if (newPerPage >= 1 && newPerPage <= 1000) {
			this.perPage = newPerPage;
			this.page = 1; // Reset alla prima pagina quando cambia la dimensione
		}
	}

	/**
	 * Imposta una nuova pagina corrente
	 *
	 * @param newPage - Nuovo numero di pagina
	 *
	 * @example
	 * const pagination = new Pagination(25, 1);
	 * pagination.setPage(3);
	 * // pagination.page = 3
	 *
	 * @public
	 */
	public setPage(newPage: number): void {
		if (newPage >= 1) {
			this.page = newPage;
		}
	}

	/**
	 * Verifica se esiste una pagina precedente
	 *
	 * @returns true se esiste una pagina precedente, false altrimenti
	 *
	 * @example
	 * const pagination = new Pagination(25, 1);
	 * pagination.hasPreviousPage(); // false
	 *
	 * const pagination2 = new Pagination(25, 2);
	 * pagination2.hasPreviousPage(); // true
	 *
	 * @public
	 */
	public hasPreviousPage(): boolean {
		return this.page > 1;
	}

	/**
	 * Verifica se esiste una pagina successiva
	 *
	 * @param totalItems - Numero totale di elementi (opzionale)
	 * @returns true se esiste una pagina successiva, false altrimenti
	 *
	 * @example
	 * const pagination = new Pagination(25, 2);
	 * pagination.hasNextPage(100); // true (pagina 2 di 4)
	 *
	 * const pagination2 = new Pagination(25, 4);
	 * pagination2.hasNextPage(100); // false (pagina 4 di 4)
	 *
	 * @public
	 */
	public hasNextPage(totalItems?: number): boolean {
		if (totalItems === undefined) {
			return true; // Non possiamo sapere senza il totale
		}

		const totalPages = Math.ceil(totalItems / this.perPage);
		return this.page < totalPages;
	}

	/**
	 * Calcola il numero totale di pagine
	 *
	 * @param totalItems - Numero totale di elementi
	 * @returns Numero totale di pagine
	 *
	 * @example
	 * const pagination = new Pagination(25, 1);
	 * pagination.getTotalPages(100); // 4 pagine
	 *
	 * const pagination2 = new Pagination(10, 1);
	 * pagination2.getTotalPages(95); // 10 pagine (ceil(95/10))
	 *
	 * @public
	 */
	public getTotalPages(totalItems: number): number {
		return Math.ceil(totalItems / this.perPage);
	}

	/**
	 * Calcola l'intervallo di elementi mostrati nella pagina corrente
	 *
	 * @param totalItems - Numero totale di elementi (opzionale)
	 * @returns Oggetto con start ed end (1-based)
	 *
	 * @example
	 * const pagination = new Pagination(25, 2);
	 * pagination.getCurrentRange(100);
	 * // { start: 26, end: 50 }
	 *
	 * const pagination2 = new Pagination(25, 4);
	 * pagination2.getCurrentRange(90);
	 * // { start: 76, end: 90 } (ultima pagina parziale)
	 *
	 * @public
	 */
	public getCurrentRange(totalItems?: number): { start: number; end: number } {
		const start = this._skip + 1;
		let end = this._skip + this.perPage;

		// Limita alla fine se conosciamo il totale
		if (totalItems !== undefined && end > totalItems) {
			end = totalItems;
		}

		return { start, end };
	}
}

/**
 * Factory function per creare oggetti di paginazione tipizzati
 * Utile per Vue 3 Composition API dove le classi possono causare problemi di reattività
 *
 * @param perPage - Numero di elementi per pagina (default: 25)
 * @param page - Numero della pagina corrente (default: 1)
 * @returns Oggetto IPaginationData tipizzato
 *
 * @example
 * // Per Vue 3 Composition API
 * const dataPagination = ref(createPaginationData(10, 1));
 *
 * @example
 * // Con valori di default
 * const dataPagination = ref(createPaginationData()); // perPage: 25, page: 1
 */
export function createPaginationData(perPage: number = 25, page: number = 1): IPaginationData {
	return {
		perPage,
		page,
	};
}
